import { isEscritorioAdmin } from '@/shared/permissions/administracao';
import { type CapabilityMatrix, resolveCapabilities } from '@/shared/permissions/capabilities';
import { audienciaPermissions, publicacaoPermissions } from '@/shared/permissions/controladoria';
import { isSuperAdmin } from '@/shared/permissions/super-admin';
import type { SessionContext } from '@/shared/permissions/types';

export function normalizeInternalReturnPath(value: string | null | undefined): string | null {
  if (!value?.startsWith('/') || value.startsWith('//')) return null;
  let url: URL;
  try {
    url = new URL(value, 'http://jurion.local');
  } catch {
    return null;
  }
  if (url.origin !== 'http://jurion.local') return null;
  const pathname = url.pathname;
  if (
    pathname === '/login' ||
    pathname === '/select-escritorio' ||
    pathname === '/change-password' ||
    pathname.startsWith('/api/v1/auth') ||
    pathname === '/_next' ||
    pathname.startsWith('/_next/')
  ) {
    return null;
  }
  return `${pathname}${url.search}${url.hash}`;
}

export type ReturnAuthorizationContext = Pick<SessionContext, 'role' | 'escritorioid'>;
type ReturnPredicate = (context: ReturnAuthorizationContext) => boolean;

const isActive: ReturnPredicate = ({ role }) => role.ativo;
const asSessionContext = (context: ReturnAuthorizationContext): SessionContext =>
  context as SessionContext;
const adaptPermission =
  (predicate: (context: SessionContext) => boolean): ReturnPredicate =>
  (context) =>
    predicate(asSessionContext(context));

// POL-02: the departmental preflight predicates derive from the ONE canonical
// capability matrix (resolveCapabilities) instead of three independently-imported
// module predicates. This removes copy #3 (return-destination) as an independent
// rule — /comercial now inherits Administração (MOD-01) automatically because the
// matrix ORs admin in — while keeping the prefix table and the ativo/'/'
// short-circuits below untouched.
const fromMatrix =
  (select: (caps: CapabilityMatrix) => boolean): ReturnPredicate =>
  (context) =>
    select(resolveCapabilities(asSessionContext(context)));

const ROUTE_CAPABILITIES: ReadonlyArray<readonly [prefix: string, allows: ReturnPredicate]> = [
  ['/controladoria/processos', isActive],
  ['/controladoria/publicacoes', adaptPermission(publicacaoPermissions.list)],
  ['/controladoria/intimacoes', adaptPermission(publicacaoPermissions.list)],
  ['/controladoria/audiencias', adaptPermission(audienciaPermissions.list)],
  ['/administracao', adaptPermission(isEscritorioAdmin)],
  ['/configuracoes', adaptPermission(isEscritorioAdmin)],
  ['/super-admin', adaptPermission(isSuperAdmin)],
  ['/comercial', fromMatrix((caps) => caps.comercial)],
  ['/financeiro', fromMatrix((caps) => caps.financeiro)],
  ['/marketing', fromMatrix((caps) => caps.marketing)],
  ['/contatos', isActive],
  ['/tarefas', isActive],
  ['/documentos', isActive],
  ['/processos', isActive],
  ['/conta', isActive],
  ['/smoke', isActive],
  ['/plano', isActive],
];

function belongsToRouteFamily(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/** Authorize known application destinations using their canonical capability predicate. */
export function authorizeReturnDestination(
  pathname: string,
  context: ReturnAuthorizationContext,
): boolean {
  if (!context.role.ativo) return false;
  if (pathname === '/') return true;
  const route = ROUTE_CAPABILITIES.find(([prefix]) => belongsToRouteFamily(pathname, prefix));
  if (!route) return false;
  try {
    return route[1](context);
  } catch {
    return false;
  }
}
